import React, { useCallback } from 'react';
import { Text, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

type ButtonVariant = 'primary' | 'secondary' | 'destructive';

interface ModalButtonProps {
  label: string;
  onPress: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  /** Whether to play the press-in scale animation. Defaults to true. */
  animateOnPress?: boolean;
  /** Show a spinner in place of the label and block presses. Keeps the button
   *  mounted at the same size so the footer never reflows between states. */
  loading?: boolean;
}

const SPRING_CONFIG = { damping: 15, stiffness: 400 };

export const ModalButton: React.FC<ModalButtonProps> = ({
  label,
  onPress,
  variant = 'primary',
  disabled = false,
  animateOnPress = true,
  loading = false,
}) => {
  const { colors, semantic, components } = useThemeColors();
  const scale = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handlePressIn = useCallback(() => {
    if (!disabled && !loading && animateOnPress) scale.value = withSpring(0.97, SPRING_CONFIG);
  }, [disabled, loading, animateOnPress, scale]);

  const handlePressOut = useCallback(() => {
    if (animateOnPress) scale.value = withSpring(1, SPRING_CONFIG);
  }, [animateOnPress, scale]);

  const variantStyles = {
    primary: {
      button: {
        backgroundColor: components.button.primaryBg,
      },
      label: {
        color: components.button.primaryText,
      },
    },
    secondary: {
      button: {
        backgroundColor: colors.glass.buttonBg,
        borderWidth: 1,
        borderColor: colors.glass.buttonBorder,
      },
      label: {
        color: semantic.text.primary,
      },
    },
    destructive: {
      button: {
        backgroundColor: components.button.dangerBg,
      },
      label: {
        color: components.button.dangerText,
      },
    },
  }[variant];

  return (
    <Animated.View style={[styles.wrapper, animatedStyle]}>
      <Pressable
        style={[
          styles.button,
          variantStyles.button,
          disabled && styles.buttonDisabled,
        ]}
        onPress={onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        disabled={disabled || loading}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled: disabled || loading, busy: loading }}
      >
        {loading ? (
          <ActivityIndicator size="small" color={variantStyles.label.color} />
        ) : (
          <Text
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.75}
            style={[
              styles.label,
              variantStyles.label,
              disabled && styles.labelDisabled,
            ]}
          >
            {label}
          </Text>
        )}
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  button: {
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  buttonDisabled: {
    opacity: 0.45,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 22,
  },
  labelDisabled: {
    opacity: 0.7,
  },
});
